/**
 * Glue between the world loop and the card-combat overlay for the
 * encounter-tile combat path (HEX-34 origin; repointed to `CardCombatOverlay` +
 * the ADR-0015 card engine in HEX-60). Slice-4 IS the combat — the V1
 * `CombatOverlay` + engine retired in the same sweep.
 *
 * The overlay owns the engine loop (`CardCombatOverlay.open`): it applies player
 * intents, auto-runs enemy turns, and reports `'victory' | 'defeat' | 'forfeit'`.
 * This flow injects an `EngineDriver` that wraps `createCombat` / `applyIntent`
 * so it can snapshot the latest `CardCombatState` — the final state carries the
 * survivor HP and victor that `resolveCombatOutcome` needs (the overlay's outcome
 * callback reports only the verdict).
 *
 * Outcome routing:
 *   - victory → render a close bar over the ended overlay; clicking it tears
 *     down, runs `resolveCombatOutcome` (HP + gold), then reports back.
 *   - defeat → render the death screen (HEX-65): character summary + deck
 *     snapshot; its Return action runs the same `close` → `resolveCombatOutcome`
 *     archives the character (stamps `Character.death`) → reports back.
 *   - forfeit (Esc) → retreat: carry the final combat HP back to the character
 *     and persist it — the combat damage STICKS (HEX-95; no free heal, ADR-0015
 *     §19) — but leave the tile UNRESOLVED so re-crossing starts a fresh fight.
 *     Tear down and report back so the host resumes the world.
 *
 * The flow does not own the world or the renderer — it owns the *combat screen
 * lifecycle*. The host (App.GameSession) keeps the world running, sets an
 * `activeOverlay` flag while `isActive()` is true to pause world input, and
 * handles the post-defeat exit-to-roster transition.
 */

import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import { maybeRespawnEncounter } from '../lifecycle/encounterRespawn';
import { type CardCombatState, applyIntent, createCombat } from './cardEngine';
import { CombatRewards } from './combatRewards';
import {
  type ResolveOutcomeDeps,
  buildEncounter,
  combatSeedFor,
  lookupEncounterEnemy,
  resolveCombatOutcome,
} from './encounter';
import type { CombatDrops, EnemyTier } from './types';
import {
  CardCombatOverlay,
  type EngineDriver,
  type CombatOutcome as OverlayOutcome,
} from './ui/CardCombatOverlay';
import { DeathScreen } from './ui/DeathScreen';

/** What the host learns when combat closes. `victory` / `defeat` route through
 * persistence; `forfeit` is a no-persistence retreat. */
export type EncounterOutcome = OverlayOutcome;

export interface EncounterFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  readonly worldSeed: number;
  readonly deps: ResolveOutcomeDeps;
  /**
   * Wall-clock stamp captured ONCE at session boot (HEX-94 / ADR-0012 §2). Drives
   * the encounter-respawn same-session gate: a resolved encounter slain in a
   * PREVIOUS session (`resolvedAt < sessionBootAt`) rolls a respawn before the
   * fight is decided, so trodden regions re-populate on return trips while
   * same-session kills can't be farmed. The stamp is the gate only — never an
   * input to the deterministic respawn roll. Omit (or undefined) to skip the
   * respawn path entirely (pre-HEX-94 behaviour; kept for callers without a
   * session stamp in scope, e.g. the map-item peek).
   */
  readonly sessionBootAt?: number;
  /** Fires after the outcome settles. Defeat: host exits to roster. */
  readonly onResolved: (outcome: EncounterOutcome) => void | Promise<void>;
}

export class EncounterFlow {
  private overlay: CardCombatOverlay | null = null;
  private state: CardCombatState | null = null;
  private closeBar: HTMLDivElement | null = null;
  private readonly rewards: CombatRewards;
  private deathScreen: DeathScreen | null = null;
  private enemyTier: EnemyTier = 'common';
  private active = false;

  constructor(private readonly container: HTMLElement) {
    this.rewards = new CombatRewards(container);
  }

  /**
   * Decide whether the tile triggers combat. Returns true if combat started
   * (overlay mounted, input should be paused); false if the tile was
   * non-combat or already resolved (host continues the walk loop).
   */
  start(opts: EncounterFlowStartOptions): boolean {
    if (this.active) return false;
    // Encounter respawn (HEX-94 / ADR-0012 §2): before reading the encounter,
    // give a previous-session resolved tile a chance to come back to life. The
    // mutation is applied in place so `buildEncounter` sees the live state on
    // the same read; persistence is flushed so the respawn survives reload.
    if (opts.sessionBootAt !== undefined) {
      const respawned = maybeRespawnEncounter(opts.tile, {
        sessionBootAt: opts.sessionBootAt,
        worldSeed: opts.worldSeed,
      });
      if (respawned) {
        void opts.deps.persistence.saveTileMutations(opts.tile);
      }
    }
    const encounter = buildEncounter(opts.character, opts.tile, opts.worldSeed);
    if (encounter === null) return false;
    const template = lookupEncounterEnemy(opts.tile);
    const drops = template.drops;
    this.enemyTier = template.tier;
    this.active = true;

    const overlay = new CardCombatOverlay();
    const driver: EngineDriver = {
      createCombat: (args) => {
        const next = createCombat(args);
        this.state = next;
        return next;
      },
      applyIntent: (state, intent) => {
        const next = applyIntent(state, intent);
        this.state = next;
        return next;
      },
    };
    overlay.open(this.container, encounter, driver, (outcome) => {
      if (outcome === 'forfeit') {
        void this.forfeit(opts);
        return;
      }
      if (outcome === 'defeat') {
        this.renderDeathScreen(opts, drops);
        return;
      }
      this.renderCloseBar(opts, outcome, drops);
    });
    this.overlay = overlay;
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  /** Test seam — current engine state, or null when inactive. */
  getState(): CardCombatState | null {
    return this.state;
  }

  destroy(): void {
    this.teardown();
  }

  private renderCloseBar(
    opts: EncounterFlowStartOptions,
    outcome: 'victory' | 'defeat',
    drops: CombatDrops,
  ): void {
    if (this.closeBar !== null) return;
    const bar = document.createElement('div');
    bar.dataset.role = 'combat-close-bar';
    bar.style.cssText =
      'position:absolute;bottom:18px;left:50%;transform:translateX(-50%);z-index:40;display:flex;gap:8px;';
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.action = 'close-combat';
    button.textContent = outcome === 'victory' ? 'Continue' : 'Return to roster';
    button.style.cssText =
      'padding:10px 14px;font-size:14px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    button.addEventListener('click', () => {
      void this.close(opts, drops);
    });
    bar.appendChild(button);
    this.container.appendChild(bar);
    this.closeBar = bar;
  }

  /** Defeat path (HEX-65): the death screen replaces the bare close bar. Built
   * from the still-live character + death tile + the slaying enemy (HP frozen at
   * the engine's final player HP). Its Return action runs the same `close` as the
   * victory bar — which archives the character (stamps `Character.death`). */
  private renderDeathScreen(opts: EncounterFlowStartOptions, drops: CombatDrops): void {
    if (this.deathScreen !== null) return;
    const state = this.state;
    const cause = state?.enemy.name ?? null;
    const hp = Math.max(0, state?.player.hp ?? 0);
    const screen = new DeathScreen(this.container);
    screen.start(
      {
        name: opts.character.name,
        discoveries: opts.character.discoveries,
        gold: opts.character.gold,
        hp,
        hex: { q: opts.tile.q, r: opts.tile.r },
        cause,
        inventory: opts.character.inventory,
      },
      () => {
        void this.close(opts, drops);
      },
    );
    this.deathScreen = screen;
  }

  private async close(opts: EncounterFlowStartOptions, drops: CombatDrops): Promise<void> {
    const state = this.state;
    if (!state || state.phase !== 'ended') return;
    const tier = this.enemyTier;
    // Tear the overlay down *before* awaiting persistence — keeps the UI
    // responsive even if a slow persistence write blocks the resolution.
    this.teardown();
    const outcome = await resolveCombatOutcome(state, opts.character, opts.tile, opts.deps, drops);
    if (outcome === 'victory') {
      // Shared victory rewards (HEX-126): combat XP (HEX-117) + tier-weighted
      // card drop + level-up overlay. resolveCombatOutcome already saved (HP
      // regen + gold); grantVictoryRewards saves again with the updated xp/level.
      await this.rewards.grantVictoryRewards({
        character: opts.character,
        tier,
        seed: combatSeedFor(opts.character, opts.tile, opts.worldSeed),
        deps: opts.deps,
      });
    }
    await opts.onResolved(outcome);
  }

  private async forfeit(opts: EncounterFlowStartOptions): Promise<void> {
    // HEX-95: a retreat keeps the combat damage. Carry the final combat HP back
    // to the character and persist it (mirroring the victory/defeat HP-writeback
    // in resolveCombatOutcome) BEFORE teardown nulls the state. Crucially the
    // tile is NOT resolved — no saveTileMutations, tile stays `revealed` — so
    // re-entry still triggers combat (ADR-0015 §19: no free heal, no penalty).
    const state = this.state;
    if (state !== null) {
      opts.character.hp = Math.max(0, state.player.hp);
      await opts.deps.persistence.saveCharacter(opts.character);
    }
    this.teardown();
    await opts.onResolved('forfeit');
  }

  private teardown(): void {
    this.overlay?.destroy();
    this.overlay = null;
    this.closeBar?.remove();
    this.closeBar = null;
    this.deathScreen?.destroy();
    this.deathScreen = null;
    this.rewards.destroy();
    this.state = null;
    this.active = false;
  }
}
